// =============================================================================
// src/chrome/search-palette/search-palette-items.ts — vocabulario da command palette.
//
// Proveniencia: porte FIEL do array `SearchPalette.ITEMS` de assets/unified-app.js
//   (2984-3017). Mesmos rotulos/subs/icones/partidos/acoes — NAO editar conteudo
//   (e o vocabulario real exibido nos 11 mockups). Tipado como uniao discriminada
//   por `action` para casar com o ramo correto em `activate` sem `any`.
//
// SRP: este arquivo so carrega os dados. Filtragem/render/navegacao ficam no index.
// =============================================================================

import type { SectionId } from '../../app/types';

/** Acao disparada ao ativar um item da palette (discrimina o ramo de `activate`). */
export type SearchPaletteAction =
  | 'region'
  | 'partido'
  | 'section'
  | 'workspace';

/** Item da palette. `icon` OU `party` definem o avatar; `section` so em action='section'. */
export interface SearchPaletteItem {
  readonly group: string;
  readonly label: string;
  readonly sub: string;
  readonly action: SearchPaletteAction;
  /** Classe de icone Tabler (ausente quando o item usa `party`). */
  readonly icon?: string;
  /** Sigla partidaria (renderiza badge com as 2 primeiras letras). */
  readonly party?: string;
  /** Secao alvo (apenas quando action === 'section'). */
  readonly section?: SectionId;
}

/**
 * Vocabulario fixo da palette: regioes, partidos e secoes.
 * Ordem preservada (o agrupamento no render respeita a ordem de aparicao).
 */
export const SEARCH_PALETTE_ITEMS: readonly SearchPaletteItem[] = [
  // Regiões (zonas e bairros aparecem nos mockups de mapa, perfil, demografia)
  { group: 'Regiões', icon: 'ti-map-pin', label: 'Zona Oeste', sub: '218 mil eleitores · tendência de alta Avante', action: 'region' },
  { group: 'Regiões', icon: 'ti-map-pin', label: 'Zona Norte', sub: '412k eleitores · disputa PL × Avante', action: 'region' },
  { group: 'Regiões', icon: 'ti-map-pin', label: 'Centro-Sul', sub: '+1.240 votos viáveis (oportunidade)', action: 'region' },
  { group: 'Regiões', icon: 'ti-map-pin', label: 'Zona Leste', sub: '328k eleitores · abstenção 19%', action: 'region' },
  { group: 'Regiões', icon: 'ti-map-pin', label: 'Centro', sub: '94k eleitores · maior densidade midiática', action: 'region' },

  // Pessoas e organizações: grupo REMOVIDO junto com o Grafo de Influencias
  // (pedido 2026-07-20) — os 4 itens só serviam para abrir o grafo.

  // Partidos (vocabulário fixo do produto)
  { group: 'Partidos', party: 'Avante', label: 'Avante', sub: '#1D9E75 · maior bancada municipal', action: 'partido' },
  { group: 'Partidos', party: 'PL', label: 'PL', sub: '#185FA5 · 2º colocado, crescendo', action: 'partido' },
  { group: 'Partidos', party: 'PT', label: 'PT', sub: '#E24B4A · forte em ZL e periferia', action: 'partido' },
  { group: 'Partidos', party: 'MDB', label: 'MDB', sub: '#639922 · base interior', action: 'partido' },
  { group: 'Partidos', party: 'PSD', label: 'PSD', sub: '#378ADD · centro político', action: 'partido' },

  // Seções navegáveis (11 telas do mockup)
  { group: 'Seções', icon: 'ti-map-2', label: 'Mapa Eleitoral', sub: 'Choropleth interativo + timeline', action: 'section', section: 'mapa-eleitoral' },
  { group: 'Seções', icon: 'ti-user-circle', label: 'Perfil da Região', sub: 'Drilldown por zona', action: 'section', section: 'perfil-regiao' },
  { group: 'Seções', icon: 'ti-versions', label: 'Comparador', sub: 'Comparar grupos lado a lado', action: 'section', section: 'comparador' },
  { group: 'Seções', icon: 'ti-chart-bar', label: 'Demografia', sub: 'Pirâmides + IDH + renda', action: 'section', section: 'demografica' },
  { group: 'Seções', icon: 'ti-layers-intersect', label: 'Estúdio de Camadas', sub: 'Combinar camadas customizadas', action: 'section', section: 'camadas' },
  { group: 'Seções', icon: 'ti-building-store', label: 'Negócios setorial', sub: 'Setor empresarial por zona', action: 'section', section: 'negocios' },
  { group: 'Seções', icon: 'ti-receipt-tax', label: 'Impostômetro', sub: 'Arrecadação municipal em tempo real', action: 'section', section: 'impostometro' },
  { group: 'Seções', icon: 'ti-activity', label: 'Monitoramento', sub: 'Dashboard de KPIs eleitorais', action: 'section', section: 'monitoramento' },
  // 'Workspace Pessoal' REMOVIDO em 2026-07-25 (aba Workspace desativada; padrao
  // do Grafo: item de palette de superficie fechada sai da lista, nao vira no-op).
  // Para religar: { group: 'Seções', icon: 'ti-briefcase', label: 'Workspace Pessoal', sub: 'Cards salvos e anotações', action: 'workspace' },
];

/** Texto da dica (coluna direita) por acao — replica o `hint` ternario legado (3133-3138). */
const HINT_BY_ACTION: Record<SearchPaletteAction, string> = {
  section: 'Ir para seção',
  region: 'Abrir região',
  partido: 'Filtrar partido',
  workspace: 'Abrir workspace',
};

/** Devolve a dica de acao de um item (greppavel; substitui o ternario inline). */
export function hintForAction(action: SearchPaletteAction): string {
  return HINT_BY_ACTION[action];
}
